import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";

ChartJS.register(ArcElement, Tooltip, Legend);

const SbiSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        question: "What is the minimum SIP amount I can invest?",
        answer:
          "The minimum SIP amount typically starts at ₹500, depending on the mutual fund scheme.",
      },
      {
        question: "Can I increase or decrease my SIP amount?",
        answer:
          "Yes, you can modify your SIP amount at any time, subject to the mutual fund's terms.",
      },
      {
        question: "Is SIP safe?",
        answer:
          "SIPs involve investing in mutual funds, and their safety depends on the type of fund. Equity funds carry higher risks but can yield higher returns, while debt funds are safer.",
      },
      {
        question: "Can I use the SBI SIP Calculator for tax-saving investments?",
        answer:
          "Yes, you can use the calculator to estimate returns from tax-saving mutual funds like ELSS.",
      },
      {
        question: "How often should I invest through SIP?",
        answer:
          "SIPs are typically monthly, but you can choose quarterly or weekly options depending on the mutual fund.",
      },
      {
        question: "Can I stop my SIP anytime?",
        answer:
          "Yes, SIPs can be stopped or paused at any time without any penalty.",
      },
      {
        question: "Is it better to start an SIP early or late?",
        answer:
          "Starting an SIP early gives you the advantage of compounding over time. The earlier you start, the more time your investment has to grow.",
      },
      {
        question: "Can I track my SIP performance?",
        answer:
          "Yes, you can track your SIP’s performance using the NAV (Net Asset Value) or by checking with your mutual fund provider.",
      },
      {
        question: "What happens if I miss an SIP payment?",
        answer:
          "Missing a payment will not stop the SIP, but that month's investment will be counted as zero. Your SIP will resume the next month.",
      },
      {
        question: "What is the difference between SIP and lump-sum investment?",
        answer:
          "SIP allows for regular, smaller investments, whereas a lump sum requires a single large investment.",
      },
      {
        question: "Can I calculate SIP returns for lump sum investments?",
        answer:
          "No, SIP is designed for regular investments, not lump sum investments. Different calculators are available for lump sum investments.",
      },
      {
        question: "Can I withdraw my SIP before the tenure ends?",
        answer:
          "Yes, you can redeem your mutual fund units at any time, but you may have to pay capital gains tax depending on the holding period.",
      },
      {
        question: "Is the SBI SIP Calculator accurate?",
        answer:
          "Yes, the calculator provides estimates based on historical performance, but actual returns may vary due to market fluctuations.",
      },
      {
        question: "Do I need to pay taxes on SIP returns?",
        answer:
          "Yes, depending on the type of fund and holding period, returns may be subject to capital gains tax.",
      },
      {
        question: "Can I invest in multiple SIPs at the same time?",
        answer:
          "Yes, you can invest in multiple SIPs, diversifying your portfolio across different mutual funds.",
      }
    ]

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
        <title>
        SBI SIP Calculator – Predict Your Mutual Fund Returns
        </title>
        <meta
          name="description"
          content="Use the SBI Bank SIP Calculator to estimate your investments’ future value. A must-have tool for mutual fund investors."
        />
    
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/sbi-bank-sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 "> SBI SIP Calculator</span>
          </li>
        </ol>
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
          SBI SIP Returns Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is the SBI SIP Calculator?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            The SBI SIP Calculator is an online tool provided by the State Bank of India (SBI) to help investors plan their Systematic Investment Plans (SIPs) in mutual funds. This calculator enables users to estimate the potential returns they can expect from their SIP investments over a specific period. It simplifies the process of calculating future corpus by considering factors such as the monthly SIP amount, expected rate of return, and investment duration. This tool is designed to make it easier for individuals to plan their investments in line with their financial goals, whether they’re saving for retirement, buying a house, or funding their child’s education.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            By inputting the basic parameters like SIP amount, expected return rate, and investment tenure, the calculator gives a clear picture of the estimated return and the final value of the investment. This helps investors make informed decisions about how much to invest each month and how long to stay invested to achieve their financial goals.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why is it Popular?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Systematic Investment Plan (SIP) is an investment strategy where individuals invest a fixed amount of money in mutual funds at regular intervals, such as monthly or quarterly. SIPs allow investors to build wealth over time through disciplined, incremental investments. The money invested is used to buy units of a mutual fund, which is a pool of money from various investors that is managed by a fund manager to generate returns.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Why is SIP Popular?
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Affordability and Flexibility:</strong> SIPs allow investors to start with as little as ₹500, making them accessible for all, irrespective of their income level. Investors have the flexibility to adjust the SIP amount as per their financial capacity.</li>
              <li><strong>Dollar-Cost Averaging:</strong> SIPs help mitigate the risk of market volatility by averaging the cost of buying units over time. This strategy ensures that investors do not have to time the market perfectly.</li>
              <li><strong>Compounding Benefits:</strong> SIPs work best over the long term. When the returns generated from an investment are reinvested, they start earning their own returns, leading to exponential growth. This is the power of compounding, which benefits long-term SIP investors.</li>
              <li><strong>Discipline:</strong> With SIPs, you invest regularly, which builds a habit of saving and investing, ensuring that you stay on track with your financial goals.</li>
              <li><strong>Variety of Mutual Funds:</strong> SIPs can be invested in various types of mutual funds, including equity, debt, hybrid, and tax-saving funds, depending on the risk profile and financial objectives of the investor.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can the SBI SIP Calculator Help You?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI SIP Calculator is a useful tool for every investor, whether you're new to investing or an experienced one. Here’s how it can help:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Precise Estimation of Future Corpus:</strong> By entering basic investment parameters, the SBI SIP Calculator provides an accurate estimate of how much your investment could grow over time. This helps in setting realistic expectations about the returns.</li>
              <li><strong>Helps in Financial Planning:</strong> The calculator helps you understand how much you need to invest each month to reach your financial goal. It can be particularly useful for long-term planning, like saving for retirement or funding your children’s education.</li>
              <li><strong>Comparison of Scenarios:</strong> The calculator allows you to compare different investment scenarios by adjusting the SIP amount, return rate, and investment duration. This gives you a clearer understanding of how changes in these variables affect your final corpus.</li>
              <li><strong>Visualization of Growth:</strong> The calculator typically presents the results in the form of graphs or tables, which make it easy to visualize how your investment grows over time. This can motivate you to stay invested and stick to your SIP plan.</li>
              <li><strong>Ease of Use:</strong> With just a few inputs, you get immediate and actionable results. It’s a quick and hassle-free way to assess your SIP investments without needing complex financial knowledge.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the SBI Mutual Fund SIP Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI Mutual Fund SIP Calculator uses a well-known formula to calculate the future value of your SIP investment. Here’s a breakdown of how it works:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Inputs Required:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Monthly SIP Contribution:</strong> The fixed amount you plan to invest regularly.</li>
              <li><strong>Expected Rate of Return:</strong> The annual return rate you anticipate for your mutual fund investment. Historically, equity mutual funds have generated higher returns, but the rate of return can vary based on market conditions.</li>
              <li><strong>Investment Duration:</strong> The length of time for which you plan to continue the SIP. Typically, the longer the tenure, the greater the power of compounding.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula for Calculation:</strong><br />
              The future value of your SIP can be calculated using the compound interest formula:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>M = Maturity amount</li>
              <li>P = SIP amount per month</li>
              <li>i = Monthly rate of return = (Annual Return ÷ 12)</li>
              <li>n = Number of months (Investment tenure)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example Calculation:</strong><br />
              Let’s say you invest ₹10,000 monthly for 5 years, with an expected return of 10% per annum. The calculator will compute the future value by applying the above formula and provide you with an estimate of how much you will accumulate by the end of the investment tenure.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI SIP Calculator also typically adjusts the calculation for compounding frequency (quarterly or annually) to provide more precise results.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the SBI SIP Calculator Online
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI SIP Calculator is user-friendly and can be used in just a few steps:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Visit the SBI SIP Calculator Tool: Go to the official SBI Mutual Fund website or the specific page where the calculator is available.</li>
              <li>Enter the SIP Amount: Input the amount you intend to invest monthly. This is the fixed amount you will contribute regularly.</li>
              <li>Enter the Expected Rate of Return: You can enter the rate of return based on your expectations from the selected mutual fund scheme. You can also use an estimated return based on historical performance.</li>
              <li>Enter the Investment Duration: Specify the duration for which you plan to keep investing. You can enter the number of years or months.</li>
              <li>Click on the Calculate Button: Once all the inputs are provided, click the ‘Calculate’ button to generate the result.</li>
              <li>View the Results: The calculator will show you the projected corpus at the end of your investment period, along with details of your monthly contribution, expected returns, and total amount invested.</li>
              <li>Adjust and Compare: You can modify the values (SIP amount, return rate, and duration) to compare different scenarios and see how changing variables affect your final investment value.</li>
            </ol>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Advantages of Using the SBI SIP Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI SIP Calculator offers several benefits to investors:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Transparency:</strong> It helps investors clearly see how their monthly contributions will grow over time, providing a transparent picture of the potential returns.</li>
              <li><strong>Helps in Goal Setting:</strong> With accurate return estimates, the calculator aids in setting realistic financial goals. Whether you're saving for retirement, a vacation, or your child’s education, it ensures that you have a roadmap to follow.</li>
              <li><strong>Easy Comparison:</strong> You can compare different investment scenarios by altering your SIP amount, expected return, and duration, helping you make more informed decisions.</li>
              <li><strong>Free of Charge:</strong> The SBI SIP Calculator is completely free to use, providing an invaluable service without any additional cost to the investor.</li>
              <li><strong>No Complex Financial Knowledge Required:</strong> The tool is designed for ease of use, requiring no deep financial understanding to operate, making it suitable for both new and experienced investors.</li>
              <li><strong>Quick Insights:</strong> You can instantly see the potential returns from your SIP, allowing you to make decisions without delays.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Which Factors Influence SIP Earnings?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several key factors can influence the returns from your SIP:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Rate of Return:</strong> The rate of return is a critical factor in determining your earnings. While historical data can guide expectations, the return may fluctuate based on market conditions and the performance of the mutual fund.</li>
              <li><strong>SIP Amount:</strong> The more you invest each month, the higher your potential returns. Consistent and higher investments usually lead to larger accumulated values.</li>
              <li><strong>Investment Duration:</strong> The longer the duration of the SIP, the more time the investments have to grow, thanks to compounding. A longer investment horizon can significantly enhance returns, especially with equity funds.</li>
              <li><strong>Market Volatility:</strong> While SIPs help smooth out market fluctuations, they cannot eliminate the impact of major market downturns. The returns depend on market conditions, especially in the short term.</li>
              <li><strong>Compounding Frequency:</strong> The frequency at which the returns are compounded—monthly, quarterly, or annually—affects the growth of your SIP. More frequent compounding usually results in better returns.</li>
            </ul>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SbiSipCalculator;
